import { defineStore } from 'pinia';
import api from '@/api';

export const useRouteStore = defineStore('route', {
  state: () => ({
    // List state with pagination
    routes: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 50,
    totalPages: 0,

    // Single route
    route: null,

    // Steps (flat list)
    steps: [],

    // Graph (steps + dependencies)
    graph: null,

    // Step type lookups
    stepTypes: [],

    // Loading states
    loading: false,
    stepsLoading: false,
    graphLoading: false,

    // Error state
    error: null
  }),

  getters: {
    getRoutes: (state) => state.routes,
    getRoute: (state) => state.route,
    getSteps: (state) => state.steps,
    getGraph: (state) => state.graph,
    getPagination: (state) => ({
      totalCount: state.totalCount,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      totalPages: state.totalPages
    }),
    isLoading: (state) => state.loading,
    isStepsLoading: (state) => state.stepsLoading,
    isGraphLoading: (state) => state.graphLoading,
    getError: (state) => state.error
  },

  actions: {
    // ====================================================================
    // Route actions
    // ====================================================================

    async fetchRoutes(params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.route.getRoutes(params);
        this.routes = resp.data.routes || [];
        this.totalCount = resp.data.totalCount;
        this.pageNumber = resp.data.pageNumber;
        this.pageSize = resp.data.pageSize;
        this.totalPages = resp.data.totalPages;
      } catch (e) {
        this.error = e;
        this.routes = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async fetchRoute(id) {
      this.loading = true;
      this.error = null;
      // Clear immediately, not just on error - a detail view watching
      // route.params.id would otherwise briefly show the previous entity.
      this.route = null;
      try {
        const resp = await api.route.getRoute(id);
        this.route = resp.data;
      } catch (e) {
        this.error = e;
        this.route = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async fetchRoutesByComponent(componentId, params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.route.getRoutesByComponent(componentId, params);
        this.routes = resp.data || [];
      } catch (e) {
        this.error = e;
        this.routes = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async createRoute(payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.route.createRoute(payload);
        this.route = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async updateRoute(id, payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.route.updateRoute(id, payload);
        this.route = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async deleteRoute(id) {
      this.loading = true;
      this.error = null;
      try {
        await api.route.deleteRoute(id);
        this.route = null;
        this.routes = this.routes.filter(r => r.routeId !== id);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async setCurrentVersion(id) {
      this.loading = true;
      this.error = null;
      try {
        await api.route.setCurrentVersion(id);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async copyRoute(id, newName, targetComponentId) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.route.copyRoute(id, newName, targetComponentId);
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    // ====================================================================
    // Step Type actions
    // ====================================================================

    async fetchStepTypes() {
      try {
        const resp = await api.route.getStepTypes();
        this.stepTypes = resp.data || [];
      } catch (e) {
        this.stepTypes = [];
      }
    },

    // ====================================================================
    // Step actions (flat list)
    // ====================================================================

    async fetchSteps(routeId) {
      this.stepsLoading = true;
      this.error = null;
      // Clear immediately, not just on error - same reasoning as fetchRoute:
      // a detail view's cost totals (reduced over this.steps) would
      // otherwise briefly show the previous route's figures while loading.
      this.steps = [];
      try {
        const resp = await api.route.getSteps(routeId);
        this.steps = resp.data || [];
      } catch (e) {
        this.error = e;
        this.steps = [];
        throw e;
      } finally {
        this.stepsLoading = false;
      }
    },

    async createStep(routeId, payload) {
      this.stepsLoading = true;
      this.error = null;
      try {
        const resp = await api.route.createStep(routeId, payload);
        this.steps.push(resp.data);
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.stepsLoading = false;
      }
    },

    async updateStep(routeId, stepId, payload) {
      this.stepsLoading = true;
      this.error = null;
      try {
        const resp = await api.route.updateStep(routeId, stepId, payload);
        const index = this.steps.findIndex(s => s.routeStepId === stepId);
        if (index >= 0) {
          this.steps[index] = resp.data;
        }
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.stepsLoading = false;
      }
    },

    async deleteStep(routeId, stepId) {
      this.stepsLoading = true;
      this.error = null;
      try {
        await api.route.deleteStep(routeId, stepId);
        this.steps = this.steps.filter(s => s.routeStepId !== stepId);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.stepsLoading = false;
      }
    },

    // ====================================================================
    // Graph (DAG) actions
    // ====================================================================

    async fetchGraph(routeId) {
      this.graphLoading = true;
      this.error = null;
      try {
        const resp = await api.route.getGraph(routeId);
        this.graph = resp.data;
      } catch (e) {
        this.error = e;
        this.graph = null;
        throw e;
      } finally {
        this.graphLoading = false;
      }
    },

    async addDependency(routeId, predecessorStepId, successorStepId) {
      try {
        const resp = await api.route.addDependency(routeId, { predecessorStepId, successorStepId });
        if (this.graph) {
          this.graph.dependencies.push(resp.data);
        }
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    async removeDependency(routeId, dependencyId) {
      try {
        await api.route.removeDependency(routeId, dependencyId);
        if (this.graph) {
          this.graph.dependencies = this.graph.dependencies.filter(d => d.dependencyId !== dependencyId);
        }
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    async updateStepPosition(routeId, stepId, x, y) {
      // Update the cached graph's own copy of the position immediately, not
      // just the backend: `nodes` in RouteGraph.vue derives every node's
      // position fresh from this.graph.steps on each recompute, and that
      // computed also depends on this.graph.dependencies (via the
      // cumulative-cost walk) - so adding/removing an edge recomputes it too.
      // Without this, a dragged-but-still-stale step would silently snap
      // back to its last-fetched position the next time the user drew a new
      // connection, even though the drag itself had already saved fine.
      const step = this.graph?.steps?.find((s) => s.routeStepId === stepId);
      if (step) {
        step.positionX = x;
        step.positionY = y;
      }
      try {
        await api.route.updateStepPosition(routeId, stepId, { x, y });
      } catch (e) {
        // Position save failures are non-critical, don't rethrow
        console.warn('Failed to save step position:', e);
      }
    },

    // Clear methods
    clearRoutes() {
      this.routes = [];
      this.totalCount = 0;
      this.pageNumber = 1;
      this.totalPages = 0;
    },

    clearRoute() {
      this.route = null;
    },

    clearSteps() {
      this.steps = [];
    },

    clearGraph() {
      this.graph = null;
    },

    clearError() {
      this.error = null;
    },

    clearAll() {
      this.clearRoutes();
      this.clearRoute();
      this.clearSteps();
      this.clearGraph();
      this.clearError();
    }
  }
});
